/**
 * Minimal task shape used to detect the default catch-all Asset rule.
 * Matches TaskInput.asset without importing generated GraphQL.
 */
export type DefaultRuleTask = {
  asset?: {
    selection?: string;
    assetTypes?: string[] | null;
  } | null;
};

/**
 * Determines if a task is the default rule task for asset-driven templates.
 * Only undefined means default rule, not empty array (empty = user hasn't selected types yet).
 * IMPORTANT: Prioritizes original task data because React Hook Form may convert undefined.
 *
 * @param task - The task input object
 * @param currentTaskType - The current task type (e.g., 'Asset')
 * @param isAssetDrivenTemplate - Whether this is an asset-driven template
 * @param assetSelection - Form value for asset selection ('all' or specific)
 * @param assetTypes - Form value for asset types (array, undefined, or null)
 * @returns true if this is the default rule task, false otherwise
 */
export function isTaskDefaultRule(
  task: DefaultRuleTask,
  currentTaskType: string | null,
  isAssetDrivenTemplate: boolean,
  assetSelection: string | undefined,
  assetTypes: string[] | undefined | null
): boolean {
  // Early guard for non-asset-driven or non-'Asset' types
  if (!isAssetDrivenTemplate || currentTaskType !== 'Asset') {
    return false;
  }

  // Primary check: Prioritize original task data (most reliable source of truth)
  // Only undefined means default rule, not empty array or null
  const taskIsDefault =
    task.asset?.selection === 'all' && task.asset?.assetTypes === undefined; // Only undefined, not empty array or null

  // Fallback check: Also check form values (form may convert undefined to null/empty array)
  // But only trust form if task doesn't have assetTypes property or it's also undefined
  const formMatchesDefault =
    assetSelection === 'all' &&
    (assetTypes === undefined ||
      assetTypes === null ||
      (Array.isArray(assetTypes) && assetTypes.length === 0)) &&
    (task.asset?.assetTypes === undefined ||
      !('assetTypes' in (task.asset || {})));

  // Result: task data is definitive, form values are fallback
  return taskIsDefault || formMatchesDefault;
}
